import cx from 'classnames';
import React from 'react';

import type {IconSource} from '../../../../design-tokens/generated/Icons';
import type {BaseSizeModifier} from '../../../types/sizes';
import type {TextVariant} from '../../../types/variants';
import {Icon} from '../../elements/Icon/Icon';
import {Text} from '../../elements/Text/Text';
import type {InteractiveElementProps} from '../InteractiveElement/InteractiveElement';
import {ListBoxLink} from '../ListBoxLink/ListBoxLink';

import styles from './ListLink.module.scss';

export const ListLink: React.FC<
    InteractiveElementProps & {
        children: React.ReactNode;
        hasBorder?: boolean;
        icon?: IconSource;
        iconSize?: React.ComponentProps<typeof Icon>['size'];
        iconAlignedStart?: boolean;
        iconClassName?: string;
        iconColor?: string;
        isDisabled?: boolean;
        isFixedIcon?: boolean;
        sizeModifier?: BaseSizeModifier;
        variant?: TextVariant;
        // Needs to be defined explicitly for TS to get the typing
        onClick?: (
            e:
                | React.MouseEvent<HTMLButtonElement>
                | React.MouseEvent<HTMLAnchorElement>
                | React.MouseEvent<HTMLDivElement>,
        ) => void;
    }
> = ({
    children,
    className,
    hasBorder = false,
    icon,
    iconAlignedStart,
    iconClassName,
    iconColor,
    iconSize = 'compact',
    isFixedIcon = false,
    sizeModifier = 'small',
    variant,
    ...props
}) => {
    const textElement = <Text variant={variant}>{children}</Text>;
    const iconElement = icon && (
        <Icon
            className={cx(
                {
                    [styles.hoverIcon]: !isFixedIcon,
                },
                'flex-shrink-0',
                iconClassName,
            )}
            color={iconColor}
            source={icon}
            size={iconSize}
        />
    );
    return (
        <ListBoxLink
            hasBorder={hasBorder}
            sizeModifier={sizeModifier}
            className={cx(styles.listLink, className)}
            {...props}
        >
            {iconAlignedStart ? (
                <div className={styles.startAlignedIconTextContainer}>
                    {iconElement}
                    {textElement}
                </div>
            ) : (
                <>
                    {textElement}
                    {iconElement}
                </>
            )}
        </ListBoxLink>
    );
};
